import { useState } from 'react';
import styled from 'styled-components';
import { useControls, useTransformEffect } from 'react-zoom-pan-pinch';
import { colors, s4, s8 } from '@ardoq/design-tokens';
import { Button, ButtonType, IconButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { Icon, IconName } from '@ardoq/icons';
import { ContentControlsProps } from './types';
import { DARK_BACKGROUND_COLOR, FULL_TRANSPARENT } from './const';
import { caption } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { IconDecorator } from '@ardoq/decorators';
import { ShapeIconOption } from '@ardoq/select';

const SvgElementType = styled.div`
  ${caption};
  color: ${colors.grey400};
`;

const ControlsWrapper = styled.div<{
  $position?: ContentControlsProps['buttonsPosition'];
}>`
  position: absolute;
  top: ${s8};
  ${({ $position }) => $position || 'right'}: ${s8};
  z-index: 1000;
`;

const StyledLink = styled(Link)`
  border-radius: ${s4};
  border: 1px solid transparent;
  background-color: ${colors.grey200};
  padding: 6px ${s8};
`;

export const ContentControls = ({
  backgroundColor,
  maxScale,
  minScale,
  zoomStep,
  link,
  onChangeBackgroundColor,
  zoomInOptions,
  buttonsPosition,
  labels: {
    zoomToLabel = 'Zoom to',
    zoomInLabel = 'Zoom in',
    zoomOutLabel = 'Zoom out',
    resetZoomLabel = 'Reset zoom and position',
    clearBackgroundTooltip = 'Clear background color',
    darkBackgroundTooltip = 'Set dark background color',
  } = {},
}: ContentControlsProps) => {
  const [zoomScale, setZoomScale] = useState(1);
  const { zoomIn, zoomOut, zoomToElement } = useControls();

  useTransformEffect(({ state }) => setZoomScale(state.scale));

  return (
    <ControlsWrapper $position={buttonsPosition} data-testid="content-controls">
      <FlexBox gap="xxsmall">
        {link && (
          <StyledLink type="secondary" href={link.href} target="_blank">
            {link.label}
          </StyledLink>
        )}
        {zoomInOptions.length > 0 && (
          <ButtonWithDropdown
            buttonType={ButtonType.TERTIARY}
            options={zoomInOptions.map(option => ({
              id: option.id,
              label: option.label,
              leftContent: <ShapeIconOption shapeId={option.shapeId} />,
              rightContent: option.type && (
                <SvgElementType>{option.type}</SvgElementType>
              ),
              onClick: () => zoomToElement(option.id, 2.5),
            }))}
          >
            <IconDecorator iconName={IconName.BULLSEYE}>
              {zoomToLabel}
            </IconDecorator>
          </ButtonWithDropdown>
        )}
        <IconButton
          iconName={IconName.ZOOM_IN}
          buttonType={ButtonType.TERTIARY}
          onClick={() => zoomIn(zoomStep)}
          isDisabled={zoomScale ? zoomScale >= maxScale : true}
          label={zoomInLabel}
        />
        <IconButton
          iconName={IconName.ZOOM_OUT}
          onClick={() => zoomOut(zoomStep)}
          buttonType={ButtonType.TERTIARY}
          isDisabled={zoomScale ? zoomScale <= minScale : true}
          label={zoomOutLabel}
        />
        <IconButton
          iconName={IconName.ZOOM_OUT_MAP}
          onClick={() => zoomToElement('imagePreviewContent', 0, 0)}
          buttonType={ButtonType.TERTIARY}
          label={resetZoomLabel}
        />
        <Button
          dataTestId="change-background-color-button-transparent"
          buttonType={ButtonType.TERTIARY}
          onClick={() => onChangeBackgroundColor('')}
          isActive={!backgroundColor || backgroundColor === ''}
          data-tooltip-text={clearBackgroundTooltip}
          aria-label={clearBackgroundTooltip}
        >
          <Icon color={FULL_TRANSPARENT} />
        </Button>
        <Button
          dataTestId="change-background-color-button-black"
          buttonType={ButtonType.TERTIARY}
          onClick={() => onChangeBackgroundColor(DARK_BACKGROUND_COLOR)}
          isActive={backgroundColor === DARK_BACKGROUND_COLOR}
          data-tooltip-text={darkBackgroundTooltip}
          aria-label={darkBackgroundTooltip}
        >
          <Icon color={DARK_BACKGROUND_COLOR} />
        </Button>
      </FlexBox>
    </ControlsWrapper>
  );
};
